import { MapPin, AlertTriangle } from "lucide-react";
import OnboardingStorePreferences from "./onboarding-store-preferences";

export interface StepThreeProps {
  /** Selected comuna name; `null` when no payload is present. */
  cityName: string | null;
  /** Selected sector name; `null` when the user picked the fallback or no data. */
  locationName: string | null;
  /** True when the user picked the `No está tu comuna?` fallback. */
  isFallback: boolean;
  /** Persisted sector/location id used to fetch existing-location store preferences. */
  locationId?: number | null;
}

/**
 * Step 3 of the onboarding wizard — confirmation step.
 *
 * Receives the selected location data via props (decoupled from
 * localStorage / the navigation hook so this component stays purely
 * presentational). When `isFallback` is true, renders a fallback marker
 * referencing the typed `No está tu comuna?` sentinel label. Otherwise
 * renders a confirmation row carrying the selected `cityName` and
 * `locationName`.
 */
const StepThree = ({
  cityName,
  locationName,
  isFallback,
  locationId,
}: StepThreeProps) => {
  return (
    <section className="flex flex-col gap-4" aria-label="Paso 3">
      <h1 className="text-2xl font-semibold leading-tight text-foreground">
         ¿Dónde haces tus compras? 
      </h1>
      <p className="text-muted-foreground leading-relaxed">
        Elige los supermercados que quieres que utilicemos para optimizar tu lista. Si tienes alguna membresía indicalo para considerar esos precios y beneficios.
      </p>
	
      {isFallback ? (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
          role="status"
          data-testid="step-three-fallback-marker"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div className="flex flex-col">
            <span className="font-semibold">No está tu comuna?</span>
            <span className="text-xs">
              Más adelante te avisaremos cuando agreguemos nuevas comunas
              disponibles cerca de ti.
            </span>
          </div>
        </div>
      ) : cityName ? (
        <div
          className="flex items-start gap-2 break-words rounded-md border border-border bg-muted/50 p-3 text-sm text-foreground"
          data-testid="step-three-selection-confirmation"
        >
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <div className="flex flex-col">
            <span className="font-semibold">{cityName}</span>
            {locationName ? (
              <span className="text-xs text-muted-foreground">
                {locationName}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
      {!isFallback && locationId != null ? (
        <div data-testid="step-three-store-preferences">
          <OnboardingStorePreferences locationId={locationId} />
        </div>
      ) : null}
    </section>
  );
};

export default StepThree;
